import { css } from '@emotion/react'
import { type Editor, isNodeEmpty } from '@tiptap/core'
import { DOMSerializer, Node } from '@tiptap/pm/model'
import { useLayoutEffect, useMemo, useRef } from 'react'
import { useTextEditor } from '../../../TextEditor/context'
import { ShimmerTextPlaceholder } from '../ShimmerTextPlaceholder'

// Hides the editor's own, still empty, view while this stands in for it.
// Below the shimmer, that view would move up when the shimmer goes away.
const hidesLiveView = css({
  '& ~ .ProseMirror': {
    display: 'none',
  },
})

// Gives no box, so the shimmer keeps the layout it has without this element
const holdsShimmer = css({ display: 'contents' })

/**
 * Parses `body` with the editor's own schema. Returns `null` for content that
 * can't be shown faithfully: an empty body, one written with an older schema,
 * or one with nodes that render through node views, whose static markup
 * differs from the live view.
 */
export const parsePlaceholderDocument = (
  editor: Editor,
  body: Record<string, unknown>
): Node | null => {
  let doc: Node
  try {
    doc = Node.fromJSON(editor.schema, body)
  } catch {
    // The synced document is the source of truth, so a body that doesn't fit
    // the current schema only means there's nothing to show early.
    return null
  }

  if (isNodeEmpty(doc)) return null

  const { nodeViews } = editor.extensionManager
  let hasNodeView = false
  doc.descendants((node) => {
    hasNodeView ||= node.type.name in nodeViews
    return !hasNodeView
  })
  if (hasNodeView) return null

  return doc
}

type PlaceholderDocumentProps = Readonly<{
  /** The document body from the REST API, which may lag the synced document */
  body?: Record<string, unknown> | null
  readOnly: boolean
}>

/**
 * Stands in for the collaborative editor until its document has synced, and
 * hides the editor's view until then. Shows `body` as static, non-editable
 * markup with the editor's classes, so the handover doesn't move anything.
 * Shows the shimmer when there's no `body` to show.
 *
 * @note Must be a child of `<TextEditorContent>` from the editor's first
 * render until sync. Tiptap appends its view after the children that exist
 * when it mounts, and the stored body can only hide a view that comes after it.
 */
export const PlaceholderDocument: React.FC<PlaceholderDocumentProps> = ({
  body,
  readOnly,
}) => {
  const { editor } = useTextEditor()
  const ref = useRef<HTMLDivElement>(null)
  const doc = useMemo(
    () => (body ? parsePlaceholderDocument(editor, body) : null),
    [editor, body]
  )

  // The same `toDOM` specs that render the live view's nodes, without an HTML
  // string in between
  useLayoutEffect(() => {
    if (!doc || !ref.current) return

    const fragment = DOMSerializer.fromSchema(editor.schema).serializeFragment(
      doc.content
    )
    const nodes = Array.from(fragment.childNodes)
    ref.current.append(fragment)
    // Only these nodes: React can insert the shimmer before this cleanup runs
    return () => nodes.forEach((node) => node.remove())
  }, [editor, doc])

  // One element in both states: React would append a new element after the
  // editor's view, since the view isn't a node that React knows about
  return (
    <div
      className={doc ? editor.view.dom.className : undefined}
      // Matches the synced view's `[contenteditable="false"]` styles
      contentEditable={doc && readOnly ? false : undefined}
      css={[hidesLiveView, !doc && holdsShimmer]}
      data-testid="placeholder-document"
      ref={ref}
    >
      {!doc && <ShimmerTextPlaceholder />}
    </div>
  )
}
